import React from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button, Input } from '@wove/web-ui'
import { X, Container } from 'lucide-react'
import { ContainerType, CreateContainerTypeRequest, UpdateContainerTypeRequest } from '@wove/api'

const containerTypeSchema = z.object({
  woveCode: z.string()
    .min(1, 'Wove code is required')
    .max(50, 'Wove code must be 50 characters or less'),
  integrationCode: z.string()
    .min(1, 'Integration code is required')
    .max(50, 'Integration code must be 50 characters or less'),
  description: z.string()
    .max(255, 'Description must be 255 characters or less')
    .optional(),
  isActive: z.boolean()
})

type FormData = z.infer<typeof containerTypeSchema>

interface ContainerTypeFormProps {
  containerType?: ContainerType
  onSubmit: (data: CreateContainerTypeRequest | UpdateContainerTypeRequest) => void
  onCancel: () => void
  isSubmitting: boolean
}

export default function ContainerTypeForm({ containerType, onSubmit, onCancel, isSubmitting }: ContainerTypeFormProps) {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(containerTypeSchema),
    defaultValues: {
      woveCode: containerType?.woveCode || '',
      integrationCode: containerType?.integrationCode || '',
      description: containerType?.description || '',
      isActive: containerType?.isActive ?? true,
    },
  })

  const watchedFields = watch(['woveCode', 'integrationCode'])

  const onFormSubmit = (data: FormData) => {
    // For update requests, we don't include the woveCode since it's in the URL
    const submitData = containerType 
      ? { 
          integrationCode: data.integrationCode,
          description: data.description || undefined,
          isActive: data.isActive 
        }
      : data

    onSubmit(submitData as CreateContainerTypeRequest | UpdateContainerTypeRequest)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full m-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Container className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">
              {containerType ? 'Edit Container Type' : 'Create Container Type'}
            </h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Wove Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Wove Code *
              </label>
              <Controller
                name="woveCode"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    disabled={isSubmitting || !!containerType} // Disable editing for existing container types
                    placeholder="e.g., 20ft, 40ft, 40HC"
                    className={`${errors.woveCode ? 'border-red-300' : ''} ${containerType ? 'bg-gray-50 text-gray-500' : ''}`}
                  />
                )}
              />
              {errors.woveCode && (
                <p className="mt-1 text-sm text-red-600">{errors.woveCode.message}</p>
              )}
              {containerType && (
                <p className="mt-1 text-xs text-gray-500">
                  Wove code cannot be changed after creation
                </p>
              )}
            </div>

            {/* Integration Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Integration Code *
              </label>
              <Controller
                name="integrationCode"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    disabled={isSubmitting}
                    placeholder="e.g., 20GP, 40GP, 40HC"
                    className={errors.integrationCode ? 'border-red-300' : ''}
                  />
                )}
              />
              {errors.integrationCode && (
                <p className="mt-1 text-sm text-red-600">{errors.integrationCode.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Code used in your external system
              </p>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <textarea
                  {...field}
                  disabled={isSubmitting}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.description ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Optional description for this container type mapping"
                />
              )}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
            )}
          </div>

          {/* Active Status */}
          <div className="flex items-center">
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={field.onChange}
                    disabled={isSubmitting}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Active
                  </span>
                </label>
              )}
            />
            <p className="ml-2 text-xs text-gray-500">
              Active container types are available for use
            </p>
          </div>

          {/* Example mapping */}
          <div className="bg-gray-50 rounded-md p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Mapping Preview</h4>
            <div className="text-sm text-gray-600">
              <div className="flex items-center justify-between mb-1">
                <span>Wove System:</span>
                <span className="font-medium">{watchedFields[0] || 'Wove Code'}</span>
              </div>
              <div className="flex items-center justify-center my-2">
                <span className="text-gray-400">↔</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Integration System:</span>
                <span className="font-medium">{watchedFields[1] || 'Integration Code'}</span>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : containerType ? 'Update Container Type' : 'Create Container Type'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}